// Phase 84.M.38 — Guest login gate context + modal.
// Khi user chưa đăng nhập click vào link/action yêu cầu auth → mở modal
// gợi ý đăng nhập / đăng ký thay vì redirect cứng sang /login.
// User flow:
//   Trang chủ /athlete/home cho phép guest truy cập (xem trận, BXH, giải).
//   Khi guest click 1 link/feature cần auth → gọi `requireAuth(actionFn)`:
//     • Nếu đã đăng nhập → chạy actionFn() ngay.
//     • Nếu chưa → mở GuestLoginGateModal.
import { createContext, useCallback, useContext, useMemo, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { AnimatePresence, m as motion } from "framer-motion";
import { X, SignIn, UserPlus } from "@phosphor-icons/react";
import { useAuth } from "./AuthContext";

const GuestLoginGateContext = createContext({
  requireAuth: (fn) => fn?.(),
});

export function GuestLoginGateProvider({ children }) {
  const { user } = useAuth();
  const [open, setOpen] = useState(false);
  const [hint, setHint] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const requireAuth = useCallback(
    (action, opts) => {
      if (user) {
        return action?.();
      }
      setHint(opts?.hint || null);
      setOpen(true);
      return undefined;
    },
    [user],
  );

  const onLogin = () => {
    setOpen(false);
    navigate("/login", {
      state: { returnTo: location.pathname + location.search },
    });
  };
  const onRegister = () => {
    setOpen(false);
    navigate("/register");
  };

  const value = useMemo(() => ({ requireAuth }), [requireAuth]);

  return (
    <GuestLoginGateContext.Provider value={value}>
      {children}
      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setOpen(false)}
            className="fixed inset-0 z-[150] flex items-end sm:items-center justify-center bg-black/80 backdrop-blur-md p-0 sm:p-4"
            data-testid="guest-gate-overlay"
          >
            <motion.div
              initial={{ y: 60, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              exit={{ y: 60, opacity: 0 }}
              transition={{ type: "spring", stiffness: 280, damping: 26 }}
              onClick={(e) => e.stopPropagation()}
              className="relative w-full sm:max-w-sm bg-gradient-to-b from-[#0B1220] to-[#060B14] border-t sm:border border-white/10 rounded-t-3xl sm:rounded-3xl shadow-2xl overflow-hidden"
              data-testid="guest-gate-modal"
            >
              {/* Glow */}
              <div
                className="pointer-events-none absolute -top-24 -right-24 w-64 h-64 rounded-full opacity-30 blur-[60px]"
                style={{ background: "radial-gradient(circle, rgba(255,138,0,0.45) 0%, transparent 70%)" }}
              />

              <div className="relative px-5 pt-5 pb-2 flex items-start justify-between">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.25em] font-mono font-bold text-[#FF8A00] mb-1">
                    PickLeague
                  </div>
                  <h2 className="text-[22px] font-black text-white leading-tight">
                    Đăng nhập để tiếp tục
                  </h2>
                </div>
                <button
                  type="button"
                  onClick={() => setOpen(false)}
                  className="w-9 h-9 rounded-full bg-white/8 hover:bg-white/15 flex items-center justify-center transition"
                  aria-label="Đóng"
                  data-testid="guest-gate-close"
                >
                  <X size={14} weight="bold" className="text-white/85" />
                </button>
              </div>

              <div className="relative px-5 pb-5 pt-1">
                <p className="text-[13px] text-white/75 leading-relaxed mb-4">
                  {hint || "Tính năng này dành cho thành viên. Đăng nhập hoặc đăng ký miễn phí để dùng đầy đủ — đăng ký giải, xem hồ sơ, theo dõi điểm trình & lịch sử thi đấu."}
                </p>

                <div className="flex flex-col gap-2.5">
                  <button
                    type="button"
                    onClick={onLogin}
                    className="w-full h-12 rounded-2xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] text-white font-black text-[13.5px] tracking-wider uppercase shadow-[0_8px_22px_rgba(255,138,0,0.4)] hover:shadow-[0_10px_28px_rgba(255,138,0,0.55)] transition active:scale-[0.98] inline-flex items-center justify-center gap-2"
                    data-testid="guest-gate-login-btn"
                  >
                    <SignIn size={16} weight="bold" />
                    Đăng nhập
                  </button>
                  <button
                    type="button"
                    onClick={onRegister}
                    className="w-full h-12 rounded-2xl bg-white/5 border border-white/15 hover:bg-white/10 text-white font-black text-[13.5px] tracking-wider uppercase transition active:scale-[0.98] inline-flex items-center justify-center gap-2"
                    data-testid="guest-gate-register-btn"
                  >
                    <UserPlus size={16} weight="bold" />
                    Tạo tài khoản mới
                  </button>
                </div>

                <p className="mt-4 text-[11px] text-white/45 text-center">
                  Bạn vẫn có thể duyệt trang chủ, xem trận đấu, giải đấu công khai mà không cần tài khoản.
                </p>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </GuestLoginGateContext.Provider>
  );
}

export function useGuestGate() {
  return useContext(GuestLoginGateContext);
}
